import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircleIcon,
  WarningCircleIcon,
  CircleNotchIcon,
  WarningIcon,
  CalendarIcon,
  FileXlsIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { useImportReportedData } from 'src/api/rpc/hooks/rebates/useHistoricalReportedImport';
import type { ImportReportedDataResponse } from '@erp/rpc';

// ============================================================================
// Types
// ============================================================================

interface SupplierMappingEntry {
  supplierReference: string;
  supplierId: string;
  agreementId: string;
}

interface DivisionMappingEntry {
  divisionReference: string;
  divisionId: string;
}

interface ReportedStep2ResultsProps {
  xlsxBase64: string;
  fileName: string;
  targetMonth: string;
  supplierMappings: SupplierMappingEntry[];
  divisionMappings: DivisionMappingEntry[];
  onComplete: () => void;
  onDone: () => void;
  onError: (error: string) => void;
  isLoading: boolean;
  error?: string;
  importComplete: boolean;
}

// ============================================================================
// Component
// ============================================================================

export default function ReportedStep2Results({
  xlsxBase64,
  fileName,
  targetMonth,
  supplierMappings,
  divisionMappings,
  onComplete,
  onDone,
  onError,
  error,
  importComplete: initialImportComplete,
}: ReportedStep2ResultsProps) {
  const [importResult, setImportResult] =
    useState<ImportReportedDataResponse | null>(null);
  const [hasStartedImport, setHasStartedImport] = useState(false);

  const importMutation = useImportReportedData();

  const handleImport = useCallback(async () => {
    try {
      const result = await importMutation.mutateAsync({
        xlsxBase64,
        targetMonth,
        supplierMappings,
        divisionMappings,
      });
      setImportResult(result);

      if (result.success) {
        onComplete();
      }
    } catch (err) {
      if (err instanceof Error) {
        onError(err.message);
      }
    }
  }, [
    xlsxBase64,
    targetMonth,
    supplierMappings,
    divisionMappings,
    importMutation,
    onComplete,
    onError,
  ]);

  // Auto-start import when component mounts (if not already completed)
  useEffect(() => {
    if (!hasStartedImport && !initialImportComplete && xlsxBase64) {
      setHasStartedImport(true);
      void handleImport();
    }
  }, [hasStartedImport, initialImportComplete, xlsxBase64, handleImport]);

  const isImporting = importMutation.isPending;
  const importSuccess = importResult?.success ?? false;
  const hasWarnings =
    (importResult?.warnings?.length ?? 0) > 0 ||
    importResult?.validationIssues.some((i) => i.severity === 'warning');
  const hasErrors = importResult?.validationIssues.some(
    (i) => i.severity === 'error',
  );

  // Format target month for display (parse as local time to avoid timezone issues)
  const formattedMonth = targetMonth
    ? (() => {
        const [year, month] = targetMonth.split('-');
        return new Date(Number(year), Number(month) - 1).toLocaleDateString(
          'en-US',
          {
            year: 'numeric',
            month: 'long',
          },
        );
      })()
    : '';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold">Import Results</h2>
          <p className="text-sm text-muted-foreground">
            {isImporting
              ? 'Importing reported data...'
              : 'Review the import results below.'}
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <CalendarIcon className="h-4 w-4" />
            <span>{formattedMonth}</span>
          </div>
          {fileName && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <FileXlsIcon className="h-4 w-4" />
              <span className="max-w-[200px] truncate">{fileName}</span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <AlertContainer variant="destructive">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </AlertContainer>
      )}

      {/* Loading State */}
      {isImporting && (
        <div className="flex flex-col items-center justify-center py-12">
          <CircleNotchIcon className="h-12 w-12 animate-spin text-primary mb-4" />
          <p className="text-lg font-medium">Importing Reported Data...</p>
          <p className="text-sm text-muted-foreground">
            Creating breakdown records and distributing amounts across rules.
          </p>
        </div>
      )}

      {/* Results */}
      {importResult && !isImporting && (
        <div className="space-y-6">
          {/* Success/Failure Banner */}
          {importSuccess ? (
            <AlertContainer className="border-green-500/50 bg-green-500/10">
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
              <AlertTitle>Import Successful</AlertTitle>
              <AlertDescription>
                Created {importResult.breakdownsCreated} breakdown records
                totaling ${importResult.totalAmount.toLocaleString()}.
              </AlertDescription>
            </AlertContainer>
          ) : (
            <AlertContainer variant="destructive">
              <WarningCircleIcon className="h-4 w-4" />
              <AlertTitle>Import Failed</AlertTitle>
              <AlertDescription>
                The import could not be completed due to validation errors.
              </AlertDescription>
            </AlertContainer>
          )}

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">
                {importResult.breakdownsCreated}
              </p>
              <p className="text-sm text-muted-foreground">
                Breakdowns Created
              </p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">
                ${importResult.totalAmount.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Total Amount</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">{importResult.skippedRows}</p>
              <p className="text-sm text-muted-foreground">Skipped Rows</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">
                {importResult.bySupplier.length}
              </p>
              <p className="text-sm text-muted-foreground">
                Suppliers Processed
              </p>
            </div>
          </div>

          {/* Warnings */}
          {hasWarnings && (
            <AlertContainer className="border-yellow-500/50 bg-yellow-500/10">
              <WarningIcon className="h-4 w-4 text-yellow-600" />
              <AlertTitle>Warnings</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4 mt-2 space-y-1">
                  {importResult.warnings.slice(0, 5).map((warning, i) => (
                    <li key={i} className="text-sm">
                      {warning}
                    </li>
                  ))}
                  {importResult.validationIssues
                    .filter((i) => i.severity === 'warning')
                    .slice(0, 5)
                    .map((issue, i) => (
                      <li key={`issue-${i}`} className="text-sm">
                        {issue.message}
                      </li>
                    ))}
                  {(importResult.warnings.length > 5 ||
                    importResult.validationIssues.filter(
                      (i) => i.severity === 'warning',
                    ).length > 5) && (
                    <li className="text-sm text-muted-foreground">
                      ...and more warnings
                    </li>
                  )}
                </ul>
              </AlertDescription>
            </AlertContainer>
          )}

          {/* Errors */}
          {hasErrors && (
            <AlertContainer variant="destructive">
              <WarningCircleIcon className="h-4 w-4" />
              <AlertTitle>Validation Errors</AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4 mt-2 space-y-1">
                  {importResult.validationIssues
                    .filter((i) => i.severity === 'error')
                    .slice(0, 10)
                    .map((issue, i) => (
                      <li key={i} className="text-sm">
                        {issue.message}
                      </li>
                    ))}
                </ul>
              </AlertDescription>
            </AlertContainer>
          )}

          {/* By Supplier Table */}
          {importResult.bySupplier.length > 0 && (
            <div>
              <h3 className="text-sm font-medium mb-2">Import by Supplier</h3>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Agreement</TableHead>
                      <TableHead className="text-right">
                        Breakdowns Created
                      </TableHead>
                      <TableHead className="text-right">Total Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {importResult.bySupplier.map((result, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono text-sm">
                          {result.supplierReference}
                        </TableCell>
                        <TableCell>{result.agreementName}</TableCell>
                        <TableCell className="text-right">
                          {result.breakdownsCreated}
                        </TableCell>
                        <TableCell className="text-right">
                          ${result.totalAmount.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Missing Agreements - in DB but not in file */}
          {importResult.missingAgreements &&
            importResult.missingAgreements.length > 0 && (
              <div>
                <AlertContainer className="border-orange-500/50 bg-orange-500/10 mb-4">
                  <WarningIcon className="h-4 w-4 text-orange-600" />
                  <AlertTitle>
                    Agreements Not in File (
                    {importResult.missingAgreements.length})
                  </AlertTitle>
                  <AlertDescription>
                    The following agreements have existing reported data for
                    this month but were NOT included in the imported file. Their
                    data remains unchanged.
                  </AlertDescription>
                </AlertContainer>
                <div className="border rounded-lg overflow-hidden border-orange-500/30">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Agreement</TableHead>
                        <TableHead className="text-right">
                          Existing Cumulative Amount
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importResult.missingAgreements.map((agreement, idx) => (
                        <TableRow key={idx} className="bg-orange-500/5">
                          <TableCell>{agreement.supplierName}</TableCell>
                          <TableCell>{agreement.agreementName}</TableCell>
                          <TableCell className="text-right font-medium">
                            $
                            {agreement.existingCumulativeAmount.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-orange-500/10 font-semibold">
                        <TableCell colSpan={2} className="text-right">
                          Total Not in File:
                        </TableCell>
                        <TableCell className="text-right">
                          $
                          {importResult.missingAgreements
                            .reduce(
                              (sum, a) => sum + a.existingCumulativeAmount,
                              0,
                            )
                            .toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end pt-4 border-t">
        <Button
          onClick={onDone}
          disabled={isImporting}
          variant={importSuccess ? 'default' : 'outline'}
        >
          {importSuccess ? 'Done' : 'Close'}
        </Button>
      </div>
    </div>
  );
}
